import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import PropTypes from "prop-types";

import ChartWrapper from "../chartWrapper/index.js";
import Key from "../key/index.js";

const curveTypes = {
  linear: d3.curveLinear,
  step: d3.curveStep,
  stepBefore: d3.curveStepBefore,
  stepAfter: d3.curveStepAfter,
  basis: d3.curveBasis,
  cardinal: d3.curveCardinal,
  monotoneX: d3.curveMonotoneX,
  catmullRom: d3.curveCatmullRom
};

const LineChart = ({ curveType, data, dimensions }) => {
  const [keyHeight, setKeyHeight] = useState(0);

  const chartContainer = useRef(null);
  const keyContainer = useRef(null);

  const { width, height, margin } = dimensions;

  const xScale = d3
    .scaleLinear()
    .domain(
      d3.extent(
        data.flatMap((d) => d.values),
        (d) => d.x
      )
    )
    .range([0, width]);

  const yScale = d3
    .scaleLinear()
    .domain([
      Math.min(
        0,
        d3.min(
          data.flatMap((d) => d.values),
          (d) => d.y
        )
      ), // Ensure domain starts at 0 or less
      Math.max(
        0,
        d3.max(
          data.flatMap((d) => d.values),
          (d) => d.y
        )
      ) // Ensure domain ends at 0 or more
    ])
    .nice() // Optionally, adjust domain to extend it to nice round values
    .range([height, 0]);

  const lineGenerator = d3
    .line()
    .x((d) => xScale(d.x))
    .y((d) => yScale(d.y))
    .curve(curveTypes[curveType]);

  // Color scale
  const colorScale = d3
    .scaleOrdinal()
    .domain(data.map((d) => d.name))
    .range(d3.schemeCategory10);

  useEffect(() => {
    if (data && chartContainer.current) {
      if (keyContainer.current) {
        setKeyHeight(
          keyContainer.current.getBoundingClientRect().height + margin.top
        );
      }

      // Clear previous SVG content and set up the main SVG container
      const svg = d3
        .select(chartContainer.current)
        .html("") // Clear any existing SVG to avoid duplications
        .append("g") // Append a 'g' element for the line chart
        .attr("class", "line-container")
        .attr(
          "transform",
          `translate(${margin.left},${margin.top + keyHeight})`
        );

      // Create or select the groups for each line
      const lineGroups = svg
        .selectAll(".line-group")
        .data(data, (d) => d.name) // Bind each line's data with a key function
        .join(
          (enter) => enter.append("g").attr("class", "line-group"), // Enter
          (update) => update, // Update (if needed, you can apply transformations here)
          (exit) => exit.remove() // Exit
        );

      // Append path within each group, utilizing the enter-update-exit pattern correctly
      lineGroups
        .append("path")
        .attr("class", "line")
        .attr("d", (d) => lineGenerator(d.values))
        .attr("fill", "none")
        .attr("stroke", (d) => colorScale(d.name))
        .attr("stroke-width", 2);
    }
  }, [
    curveType,
    data,
    dimensions,
    xScale,
    yScale,
    lineGenerator,
    keyContainer
  ]); // Dependencies updated to include lineGenerator

  return (
    <ChartWrapper
      axes={{
        xScale,
        yScale,
        height,
        width,
        margin: { ...margin, top: margin.top + keyHeight }
      }}
      height={height + (margin.top + keyHeight) + margin.bottom}
      width={width + margin.left + margin.right}
    >
      <Key
        forwardedRef={keyContainer}
        data={data}
        colorScale={colorScale}
        margin={margin}
        width={width}
      />
      <g ref={chartContainer} />
    </ChartWrapper>
  );
};

LineChart.propTypes = {
  curveType: PropTypes.oneOf([
    "linear",
    "step",
    "stepBefore",
    "stepAfter",
    "basis",
    "cardinal",
    "monotoneX",
    "catmullRom"
  ]),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          x: PropTypes.number.isRequired,
          y: PropTypes.number.isRequired
        })
      ).isRequired
    })
  ).isRequired,
  dimensions: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    margin: PropTypes.shape({
      top: PropTypes.number,
      right: PropTypes.number,
      bottom: PropTypes.number,
      left: PropTypes.number
    }).isRequired
  }).isRequired
};

LineChart.defaultProps = {
  curveType: "monotoneX", // Default curve type
  dimensions: {
    width: 500,
    height: 300,
    margin: { top: 20, right: 30, bottom: 30, left: 50 }
  }
};

export default LineChart;
